import React, { useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";

/**
 * UpdateBanner — shown when the SW activates a new app version.
 *
 * The service worker sends { type: 'NEW_VERSION' } via postMessage
 * after activating and clearing old caches. The banner prompts the
 * user to reload without forcing it on them mid-task.
 *
 * Also handles ChunkLoadError (lazy import fails after a deploy replaces
 * hashed chunk filenames). On the first ChunkLoadError the page reloads
 * automatically — if the same error happens again after reload (broken
 * deploy), the banner shows instead to avoid an infinite reload loop.
 */
export default function UpdateBanner() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    // ChunkLoadError: a lazy chunk failed to load, usually because the SW
    // activated a new deploy while the app was backgrounded and deleted the
    // old chunk hashes. Never auto-reload — let the user decide when to
    // interrupt their work.
    const handleError = (event) => {
      const msg = event.message || event.reason?.message || "";
      if (
        msg.includes("ChunkLoadError") ||
        msg.includes("Loading chunk") ||
        msg.includes("Failed to fetch dynamically imported module")
      ) {
        setShow(true);
      }
    };

    const handleRejection = (e) =>
      handleError({ message: e.reason?.message, reason: e.reason });

    // SW activate emits NEW_VERSION after claiming clients and purging old caches.
    const handleSWMessage = (event) => {
      if (event.data?.type === 'NEW_VERSION') {
        setShow(true);
      }
    };

    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleRejection);
    navigator.serviceWorker?.addEventListener('message', handleSWMessage);

    return () => {
      window.removeEventListener("error", handleError);
      window.removeEventListener("unhandledrejection", handleRejection);
      navigator.serviceWorker?.removeEventListener('message', handleSWMessage);
    };
  }, []);

  if (!show) return null;

  return (
    <div className="fixed bottom-[calc(96px+env(safe-area-inset-bottom)+8px)] left-4 right-4 z-50 animate-fade pointer-events-none">
      <div className="pointer-events-auto bg-slate-900 text-white rounded-[20px] px-4 py-3 flex items-center gap-3 shadow-[0_8px_32px_rgba(0,0,0,0.25)]">
        <div className="h-8 w-8 rounded-full bg-brand flex items-center justify-center shrink-0">
          <RefreshCw size={14} />
        </div>
        <p className="text-[13px] font-medium flex-1 leading-snug">
          Nueva versión disponible
        </p>
        <button
          onClick={() => window.location.reload()}
          className="text-[12px] font-bold text-brand-light bg-brand/20 rounded-[10px] px-3 py-1.5 active:scale-[0.97] transition-all duration-150"
        >
          Actualizar
        </button>
        <button
          onClick={() => setShow(false)}
          className="text-[11px] text-slate-400 pl-1 active:opacity-60"
          aria-label="Cerrar"
        >
          ✕
        </button>
      </div>
    </div>
  );
}
